// ============================================================================
// TASKAPP.JS - BUCLE INFINITO COMPLETAMENTE CORREGIDO
// SOLUCION: useMemo para estabilizar referencias + useCallback + control de actualizaciones
// ============================================================================

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTaskStore } from '../store/TaskStore';
import clientConfigService from '../services/clientConfigService';
import { getTaskPath } from '../utils/taskRef';
import PageTransition from './PageTransition';
import ModuleErrorBoundary from './ModuleErrorBoundary';
import lazyRetry from '../utils/lazyRetry';
import {
  LoadingOverlay,
  ErrorDisplay,
  TasksDashboard,
  TaskDetailContainer,
  TaskCreateContainer,
  TaskEditContainer
} from './taskAppContainers';

// Importar el provider de archivos
import { FileAttachmentsProvider } from './FileAttachmentsContext';

// Importar componentes de autenticación
import { AdminRoute } from '../auth/PrivateRoute';
import { useAuth } from '../auth/AuthContext';

// CSS de Documentación que se queda en el bundle inicial aunque la pantalla
// vaya lazy: tiene reglas globales (.spin del fallback de carga, .task-name del
// sprint, .btn-primary, .search-icon, .doc-back del detalle de tarea) que hoy
// pintan pantallas del main. Mismo orden que tenía en main.css (revisión
// 2026-10, MEDIA-3). Salen con su chunk solo los CSS acotados a su pantalla.
import '../styles/documentation-detail.css';
import '../styles/documentation-app.css';
import '../styles/tasks.css';

// Pantallas secundarias bajo demanda (revisión 2026-10, informe 05, MEDIA-3):
// cada una solo se usa en su ruta, así que su JS y su CSS salen del bundle
// inicial. Mismo patrón que los addons (lazyRetry + ModuleShell). El formulario
// de tarea va lazy dentro de taskAppContainers.js.
const UserManagement = lazy(() => lazyRetry(() => import('../auth/UserManagement'), 'UserManagement'));
const DocumentationApp = lazy(() => lazyRetry(() => import('./DocumentationApp'), 'DocumentationApp'));
const ClientConfig = lazy(() => lazyRetry(() => import('./ClientConfig'), 'ClientConfig'));
const UserProfile = lazy(() => lazyRetry(() => import('./UserProfile'), 'UserProfile'));

// Módulos pesados (recharts / vistas secundarias) cargados bajo demanda:
// salen del bundle inicial y solo se descargan al entrar en su ruta.
const SprintHistoryPage = lazy(() => lazyRetry(() => import('./SprintHistoryPage'), 'SprintHistoryPage'));
const CrawlApp = lazy(() => lazyRetry(() => import('./Crawl/CrawlApp'), 'CrawlApp'));
const GadsApp = lazy(() => lazyRetry(() => import('./GadsPanel/GadsApp'), 'GadsApp'));
const SeoApp = lazy(() => lazyRetry(() => import('./SeoPanel/SeoApp'), 'SeoApp'));
const ChangelogApp = lazy(() => lazyRetry(() => import('./Changelog/ChangelogApp'), 'ChangelogApp'));
const MetaApp = lazy(() => lazyRetry(() => import('./MetaPanel/MetaApp'), 'MetaApp'));
const TikTokApp = lazy(() => lazyRetry(() => import('./TikTokPanel/TikTokApp'), 'TikTokApp'));
const CreativesApp = lazy(() => lazyRetry(() => import('./Creatives/CreativesApp'), 'CreativesApp'));
const AiVisApp = lazy(() => lazyRetry(() => import('./AiVisibility/AiVisApp'), 'AiVisApp'));
const OneSearchApp = lazy(() => lazyRetry(() => import('./OneSearch/OneSearchApp'), 'OneSearchApp'));

// Fallback de carga de los módulos lazy (el mismo dentro y fuera de AnimatePresence).
const LAZY_FALLBACK = <div className="task-app-lazy-loading" style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}><Loader2 className="spin" size={22} /></div>;

// Envoltorio de cada ruta lazy: transición, boundary del módulo y un Suspense
// PROPIO dentro del hijo de presencia. Si el único Suspense quedara fuera de
// AnimatePresence, al suspender el módulo entrante React ocultaría el árbol de
// AnimatePresence a mitad de la salida y la vista podía quedar desincronizada
// de la URL (revisión 2026-10, informe 01, MEDIA-1).
function ModuleShell({ resetKey, children }) {
  return (
    <PageTransition>
      <ModuleErrorBoundary resetKey={resetKey}>
        <Suspense fallback={LAZY_FALLBACK}>{children}</Suspense>
      </ModuleErrorBoundary>
    </PageTransition>
  );
}

// Componente interno envuelto en el provider
function TaskAppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isManager } = useAuth();

  const {
    viewMode,
    currentTask,
    isLoading,
    isInitializing,
    error,
    tasks,
    setCurrentTask,
    setViewMode,
    clearError,
    fetchTask,
    initialize
  } = useTaskStore();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Addons activos del tenant. Fuente compartida con la barra de pestañas
  // (clientConfigService): Layout ya dispara el fetch; aquí solo nos
  // suscribimos para que la guarda de ruta reaccione al instante cuando un
  // admin activa/desactiva un módulo desde Configuración.
  const [addons, setAddons] = useState(
    () => clientConfigService.getConfig()?.addons || { crawl: true, gads: true }
  );
  // Config del tenant resuelta (llegada o fallida). Hasta entonces las rutas
  // con guarda pintan el marcador de carga en vez de redirigir: con el valor
  // por defecto, un enlace directo a /task/seo o /task/changelog rebotaba al
  // tablero antes de saber que el addon está activo (R2, 4-oct-2026).
  const [configReady, setConfigReady] = useState(() => clientConfigService.isLoaded());
  useEffect(() => {
    let mounted = true;
    const apply = (cfg) => setAddons(cfg?.addons || { crawl: true, gads: true });
    apply(clientConfigService.getConfig());
    const unsubscribe = clientConfigService.subscribe((cfg) => {
      apply(cfg);
      setConfigReady(true);
    });
    // fetchConfig comparte la petición de Layout (single-flight) y nunca
    // rechaza: si falla, se decide con el valor por defecto como antes.
    clientConfigService.fetchConfig().then(() => {
      if (!mounted) return;
      apply(clientConfigService.getConfig());
      setConfigReady(true);
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // 🔧 INICIALIZACIÓN MEJORADA - Sin dependencia de autenticación
  useEffect(() => {
    let mounted = true;
    
    const initializeApp = async () => {
      try {
        console.log('TaskApp: Initializing without auth dependency...');
        
        // Limpiar errores previos
        if (error) {
          clearError();
        }
        
        // Inicializar TaskStore sin esperar autenticación
        // El TaskStore manejará la autenticación internamente
        if (initialize) {
          await initialize();
        }
        
        if (mounted) {
          setIsInitialized(true);
          console.log('TaskApp: Initialized successfully');
        }
      } catch (error) {
        console.error('TaskApp: Initialization failed:', error);
        if (mounted) {
          setIsInitialized(true); // Continuar aunque falle la inicialización
        }
      }
    };
    
    initializeApp();
    
    return () => {
      mounted = false;
    };
  }, []); // Sin dependencias para evitar loops

  console.log('Location debug:', {
    pathname: location.pathname,
    currentTask: currentTask?.id,
    viewMode: viewMode
  });

  // Manejar navegación hacia vista detalle
  const handleTaskSelect = async (task) => {
    if (!task || isTransitioning) return;
    
    setIsTransitioning(true);
    
    try {
      console.log('Opening task detail:', task.nombre);

      setCurrentTask(task);
      navigate(getTaskPath(task));
      
      // Scroll al top para vista detalle
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error opening task detail:', error);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Manejar navegación de vuelta a la tabla
  const handleBackToTable = async () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    try {
      console.log('Returning to task table');

      setCurrentTask(null);
      setViewMode('table');
      navigate('/task');
      
    } catch (error) {
      console.error('Error returning to table:', error);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Manejar navegación a documentación
  const handleOpenDocumentation = () => {
    console.log('Opening documentation');
    navigate('/task/documentation');
  };


  // Marcador de carga de las rutas con guarda mientras llega la config. Misma
  // envoltura que el módulo (ModuleShell) para que, al resolverse, solo cambie
  // el hijo y AnimatePresence no desmonte la pantalla.
  const pendingModule = <ModuleShell resetKey={location.pathname}>{LAZY_FALLBACK}</ModuleShell>;

  return (
    <div className={`task-app ${isTransitioning ? 'transitioning' : ''}`}>
      {/* Overlay de carga global - SOLO durante la carga inicial del store
          (isInitializing), NO en cada micro-mutación. Las mutaciones usan
          isLoading (que sigue vivo para estados disabled), pero ya no disparan
          este overlay a pantalla completa. */}
      {isInitialized && isInitializing && <LoadingOverlay />}
      
      {/* Display de error global */}
      {error && <ErrorDisplay error={error} onRetry={clearError} />}

      {/* Si la app no está inicializada, mostrar loading general */}
      {!isInitialized ? (
        <div className="loading-overlay">
          <div className="loading-content">
            <Loader2 size={32} className="loading-spinner" />
            <p className="loading-text">Inicializando aplicación...</p>
          </div>
        </div>
      ) : (
        <div className="task-app-content">
          {/* RUTAS - con transición premium entre pantallas (AnimatePresence).
              mode="wait": la pantalla saliente termina su animación antes de
              entrar la nueva. La key por pathname es lo que dispara la
              transición en cada navegación.
              Suspense: fallback mientras se descarga un módulo lazy (Crawl,
              gads-panel, changelog, sprint-history). */}
          <Suspense fallback={<div className="task-app-lazy-loading" style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}><Loader2 className="spin" size={22} /></div>}>
          <AnimatePresence mode="wait" initial={false}>
            {/* Key por SECCIÓN (no por pathname completo): la transición se
                dispara al saltar entre pantallas de primer nivel (dashboard,
                detalle, docs, usuarios...), pero la navegación interna de una
                sección (sub-rutas de documentación, cambiar de tarea) no
                re-monta ni vuelve a animar la pantalla entera. */}
            <Routes location={location} key={location.pathname.split('/')[2] || 'index'}>
              {/* Dashboard principal - Ruta vacía coincide con /task */}
              <Route index element={<PageTransition><TasksDashboard onTaskSelect={handleTaskSelect} onOpenDocumentation={handleOpenDocumentation} /></PageTransition>} />

              {/* Detalle de tarea - /task/detail/:taskId */}
              <Route path="detail/:taskId" element={<PageTransition><TaskDetailContainer isInitialized={isInitialized} navigate={navigate} currentTask={currentTask} tasks={tasks} setCurrentTask={setCurrentTask} fetchTask={fetchTask} isTransitioning={isTransitioning} handleBackToTable={handleBackToTable} /></PageTransition>} />

              {/* Gestión de usuarios (solo admins) - /task/users */}
              <Route
                path="users"
                element={
                  <ModuleShell resetKey={location.pathname}>
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  </ModuleShell>
                }
              />

              {/* Crear nueva tarea - /task/create */}
              <Route path="create" element={<ModuleShell resetKey={location.pathname}><TaskCreateContainer /></ModuleShell>} />

              {/* Editar tarea existente - /task/edit/:taskId */}
              <Route path="edit/:taskId" element={<ModuleShell resetKey={location.pathname}><TaskEditContainer /></ModuleShell>} />

              {/* Documentación - /task/documentation/* */}
              <Route
                path="documentation/*"
                element={
                  <ModuleShell resetKey={location.pathname}>
                    <DocumentationApp
                      onBack={handleBackToTable}
                    />
                  </ModuleShell>
                }
              />
              {/* Configuración de Cliente (solo admins) - /task/client-config */}
              <Route
                path="client-config"
                element={
                  <ModuleShell resetKey={location.pathname}>
                    <AdminRoute>
                      <ClientConfig />
                    </AdminRoute>
                  </ModuleShell>
                }
              />
              <Route path="/profile" element={<ModuleShell resetKey={location.pathname}><UserProfile /></ModuleShell>} />

              {/* Histórico de sprints - pantalla propia */}
              <Route path="sprint-history" element={<ModuleShell resetKey={location.pathname}><SprintHistoryPage onTaskSelect={handleTaskSelect} /></ModuleShell>} />

              {/* Módulo Crawl (absorción de la suite) - /task/crawl/*.
                  Si el addon está desactivado para el cliente, la ruta redirige
                  al dashboard (no se accede a un módulo apagado por URL). */}
              <Route path="crawl/*" element={!configReady ? pendingModule : addons.crawl ? <ModuleShell resetKey={location.pathname}><CrawlApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo gads-panel (Google Ads) - /task/gads-panel/* */}
              <Route path="gads-panel/*" element={!configReady ? pendingModule : addons.gads ? <ModuleShell resetKey={location.pathname}><GadsApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo Changelog (historial de cambios de Google Ads) - /task/changelog/* */}
              <Route path="changelog/*" element={!configReady ? pendingModule : addons.changelog ? <ModuleShell resetKey={location.pathname}><ChangelogApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo meta-panel (Meta Ads) - /task/meta/*. Addon opt-in:
                  con el addon activo aparece como pestaña para el cliente; los
                  admin/manager entran siempre por URL aunque esté desactivado
                  (uso interno pre-lanzamiento). El backend replica este gate. */}
              <Route path="meta/*" element={!configReady ? pendingModule : (addons.meta || isAdmin() || isManager()) ? <ModuleShell resetKey={location.pathname}><MetaApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo tiktok-panel (TikTok Ads) - /task/tiktok/*. Mismo gate
                  que Meta: pestaña con el addon activo; admin/manager entran
                  siempre por URL. El backend replica este gate. */}
              <Route path="tiktok/*" element={!configReady ? pendingModule : (addons.tiktok || isAdmin() || isManager()) ? <ModuleShell resetKey={location.pathname}><TikTokApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo seo-panel (Search Console) - /task/seo/* */}
              <Route path="seo/*" element={!configReady ? pendingModule : addons.seo ? <ModuleShell resetKey={location.pathname}><SeoApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo Creatividades - /task/creatives/*. Addon opt-in (hoy solo
                  Skello); admin/manager entran siempre por URL (uso interno). El
                  backend replica este gate. */}
              <Route path="creatives/*" element={!configReady ? pendingModule : (addons.creatives || isAdmin() || isManager()) ? <ModuleShell resetKey={location.pathname}><CreativesApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo Visibilidad IA - /task/aivis/*. Addon opt-in (foto del
                  sector cargada a mano cada ~3 meses); admin/manager entran
                  siempre por URL aunque esté desactivado (revisión interna
                  pre-lanzamiento, mismo patrón que Meta/Creatividades). */}
              <Route path="aivis/*" element={!configReady ? pendingModule : (addons.aivis || isAdmin() || isManager()) ? <ModuleShell resetKey={location.pathname}><AiVisApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Módulo OneSearch (salud de marca: Search Console + Ads de
                  marca) - /task/onesearch/*. En beta: addon apagado para el
                  cliente y admin/manager entrando por URL, que ven el panel
                  marcado como tal. El backend replica este gate. */}
              <Route path="onesearch/*" element={!configReady ? pendingModule : (addons.onesearch || isAdmin() || isManager()) ? <ModuleShell resetKey={location.pathname}><OneSearchApp /></ModuleShell> : <Navigate to="/task" replace />} />

              {/* Catch-all para cualquier ruta no reconocida bajo /task */}
              <Route path="*" element={<PageTransition><TasksDashboard onTaskSelect={handleTaskSelect} onOpenDocumentation={handleOpenDocumentation} /></PageTransition>} />
            </Routes>
          </AnimatePresence>
          </Suspense>
        </div>
      )}

      {/* Debug info en desarrollo - Solo si está inicializada */}
      {process.env.NODE_ENV === 'development' && isInitialized && (
        <div className="debug-info">
          <details>
            <summary>Debug Info</summary>
            <pre>
              App Initialized: {isInitialized.toString()}{'\n'}
              View Mode: {viewMode}{'\n'}
              Current Task: {currentTask?.id || 'None'}{'\n'}
              Location: {location.pathname}{'\n'}
              Is Loading: {isLoading.toString()}{'\n'}
              Is Transitioning: {isTransitioning.toString()}{'\n'}
              Tasks Count: {tasks?.length || 0}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

// Componente principal envuelto en el FileAttachmentsProvider
function TaskApp() {
  return (
    <FileAttachmentsProvider>
      <TaskAppInner />
    </FileAttachmentsProvider>
  );
}

export default TaskApp;